<script setup lang="ts">
/**
 * Puida's wavy section separator (design.md §11a), the Vue counterpart of
 * puida.com's `SectionSeparator.astro`. The wave hangs from the section above
 * (`fromColor`) over whatever is below, with the page palette's complement as
 * the accent ribbon. A fresh shape is rolled per mount, as puida.com rolls one
 * per load.
 */
import { computed } from 'vue';
import { generateSeparator, layerFills } from '@sulje/brand';
import { pagePalette } from '@/lib/palette';

const props = withDefaults(defineProps<{ fromColor: string; color?: string; height?: number }>(), {
  color: undefined,
  height: 100,
});

// The SHAPE is rolled once per mount; the COLOURS follow the props. The
// footer outlives navigation, so a fill captured at setup kept the landing
// page's black under every later page.
const paths = generateSeparator(false);
const layers = computed(() =>
  layerFills(props.color ?? pagePalette.complement, props.fromColor).map((l) => ({
    d: paths[l.path],
    fill: l.fill,
  })),
);
</script>

<template>
  <div aria-hidden="true" class="overflow-hidden leading-none" :style="{ height: `${height}px` }">
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" class="block h-full w-full">
      <g transform="translate(0, 1)">
        <path v-for="(layer, i) in layers" :key="i" :d="layer.d" :style="{ fill: layer.fill }" />
      </g>
      <rect width="100" height="3" :style="{ fill: fromColor }" />
    </svg>
  </div>
</template>
